// components/checkout/Checkout.jsx - FINAL VERSION WITH ADDRESS SERVICE & CANCEL PAYMENT
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import CheckoutHeroBit from '../components/checkout/CheckoutHeroBit';
import StepIndicator from '../components/checkout/StepIndicator';
import AddressForm from '../components/checkout/AddressForm';
import ShippingOptions from '../components/checkout/ShippingOptions';
import PaymentMethod from '../components/checkout/PaymentMethod';
import OrderSummary from '../components/checkout/OrderSummary';
import LuxuryButton from '../components/ui/LuxuryButton';
import LuxuryLoading from '../components/ui/LuxuryLoading';
import {
    FaArrowLeft,
    FaArrowRight,
    FaCheckCircle,
    FaShieldAlt,
    FaExclamationCircle,
    FaTimes,
    FaLock,
    FaCreditCard,
    FaShoppingBag,
    FaHistory,
    FaSpinner,
    FaMapMarkerAlt,
    FaSave
} from 'react-icons/fa';
import '../styles/globals.css';
import '../styles/checkout.css';
import { checkoutPageTransition, stepTransition } from "../components/animations/checkoutAnimations";
import { CheckoutService } from '../api/checkout.api';
import { PaymentService } from '../api/payment.api';
import { AuthService } from '../api/auth.api';
import { CartService } from '../api/cart.api';
import { OrderService } from '../api/order.api';
import { SiRazorpay } from "react-icons/si";
import { UserService } from "../api/user.api";

// Debug logging utility
const debugLog = (component, action, data = null) => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    console.group(`🔍 [${timestamp}] ${component}`);
    console.log(`🏷️  Action: ${action}`);
    if (data !== null) {
        console.log('📦 Data:', data);
    }
    console.groupEnd();
};

const Checkout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(true);
    const [isScrolled, setIsScrolled] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [cart, setCart] = useState({ items: [], subtotal: 0 });
    const [shippingMethods, setShippingMethods] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [activeOrder, setActiveOrder] = useState(null);
    const [canCancelPayment, setCanCancelPayment] = useState(false);

    // Payment processing states
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [createdOrder, setCreatedOrder] = useState(null);
    const [paymentIntent, setPaymentIntent] = useState(null);
    const [recoveringPayment, setRecoveringPayment] = useState(false);
    const [paymentRetry, setPaymentRetry] = useState(false);

    // Loading states
    const [cartReady, setCartReady] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [totalsCalculated, setTotalsCalculated] = useState(false);

    // Form states
    const [address, setAddress] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'India'
    });

    const [shippingMethod, setShippingMethod] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('razorpay');
    const [coupon, setCoupon] = useState(null);
    const [appliedOffers, setAppliedOffers] = useState([]);
    const [totals, setTotals] = useState({
        subtotal: 0,
        discount: 0,
        shippingCost: 0,
        tax: 0,
        total: 0
    });

    // Address management states
    const [userAddresses, setUserAddresses] = useState([]);
    const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
    const [isSavingAddress, setIsSavingAddress] = useState(false);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);
    const [saveNewAddress, setSaveNewAddress] = useState(true);

    // Use refs
    const hasInitialized = useRef(false);
    const hasCheckedAuth = useRef(false);
    const hasMergedCart = useRef(false);
    const razorpayLoaded = useRef(false);
    const initialTotalsCalculated = useRef(false);

    // 🔄 Clear messages after 5 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            if (error) setError('');
            if (success) setSuccess('');
        }, 5000);
        return () => clearTimeout(timer);
    }, [error, success]);

    // 💾 Save address to sessionStorage
    useEffect(() => {
        if (address.name) {
            sessionStorage.setItem('kromaic_checkout_form_data', JSON.stringify(address));
        }
    }, [address]);

    // 📜 Scroll handling
    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Add useEffect to check cancellable status
    useEffect(() => {
        const checkCancelStatus = async () => {
            if (activeOrder && !paymentProcessing) {
                try {
                    const response = await PaymentService.checkCancellable(activeOrder._id);
                    setCanCancelPayment(response.data?.canCancel || false);
                } catch (error) {
                    debugLog('Checkout', 'Check cancellable error:', error.message);
                    setCanCancelPayment(false);
                }
            } else {
                setCanCancelPayment(false);
            }
        };

        if (activeOrder) {
            checkCancelStatus();
        }
    }, [activeOrder, paymentProcessing]);

    // 🔐 Main initialization effect
    useEffect(() => {
        const initializeCheckout = async () => {
            if (hasInitialized.current) return;

            debugLog('Checkout', '🔄 Starting checkout initialization...');

            try {
                // Step 1: Authentication check
                debugLog('Checkout', '🔐 Checking authentication...');
                const token = AuthService.getStoredToken();
                if (!token) {
                    redirectToLogin();
                    return;
                }

                const authResult = await AuthService.checkAuth();
                if (!authResult?.authenticated) {
                    redirectToLogin();
                    return;
                }

                hasCheckedAuth.current = true;
                debugLog('Checkout', '✅ Authentication successful');

                // Step 2: Check for active orders
                debugLog('Checkout', '📋 Checking for active orders...');
                await checkActiveOrders();

                // Step 3: Load cart and merge if needed
                debugLog('Checkout', '🛒 Loading cart data...');
                await tryMergeCart();

                // Step 4: Load checkout data (only if no active order)
                if (!activeOrder) {
                    debugLog('Checkout', '📊 Loading checkout data...');
                    await loadCheckoutData();
                }

                // Mark as initialized
                hasInitialized.current = true;
                setIsAuthenticating(false);
                setDataLoaded(true);

                debugLog('Checkout', '🎉 Checkout fully initialized');

            } catch (error) {
                debugLog('Checkout', '❌ Initialization error:', error.message);
                setError('Failed to initialize checkout. Please refresh the page.');
                setIsAuthenticating(false);
            }
        };

        initializeCheckout();
    }, []);

    // 📋 Check for active/pending orders
    const checkActiveOrders = useCallback(async () => {
        try {
            debugLog('Checkout', 'Checking for active orders...');

            // Try to get user orders - handle both function formats
            let ordersResponse;
            try {
                if (typeof OrderService.getUserOrders === 'function') {
                    ordersResponse = await OrderService.getUserOrders();
                } else if (OrderService.getOrders) {
                    ordersResponse = await OrderService.getOrders();
                } else {
                    // Try direct API call if service methods don't exist
                    const token = AuthService.getStoredToken();
                    const response = await fetch('/api/orders/my-orders', {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    if (response.ok) {
                        ordersResponse = await response.json();
                    }
                }
            } catch (apiError) {
                debugLog('Checkout', 'Order API error, continuing:', apiError.message);
                return;
            }

            if (!ordersResponse) return;

            // Handle different response structures
            const orders = ordersResponse.data || ordersResponse.orders || ordersResponse || [];

            // Filter for pending/processing orders
            const pendingOrders = orders.filter(order => {
                const status = order.paymentStatus || order.status;
                return ['pending', 'processing', 'created'].includes(status);
            });

            if (pendingOrders.length > 0) {
                const latestOrder = pendingOrders[0];
                debugLog('Checkout', 'Found active order:', latestOrder.orderNumber || latestOrder.id);

                // Get detailed order info
                let orderDetails;
                try {
                    if (typeof OrderService.getOrderById === 'function') {
                        orderDetails = await OrderService.getOrderById(latestOrder._id || latestOrder.id);
                    } else {
                        const token = AuthService.getStoredToken();
                        const response = await fetch(`/api/orders/${latestOrder._id || latestOrder.id}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });
                        if (response.ok) {
                            orderDetails = await response.json();
                        }
                    }
                } catch (detailError) {
                    debugLog('Checkout', 'Order details error:', detailError.message);
                    orderDetails = latestOrder;
                }

                const order = orderDetails?.data || orderDetails || latestOrder;

                // If payment completed, redirect to success
                if (order.paymentStatus === 'completed' || order.paymentStatus === 'paid') {
                    navigate('/order-success', {
                        state: {
                            orderId: order._id || order.id,
                            orderNumber: order.orderNumber,
                            amount: order.total
                        },
                        replace: true
                    });
                    return;
                }

                // Set as active order for recovery
                setActiveOrder(order);
                setRecoveringPayment(true);

                // Pre-fill form data from order
                if (order.shippingAddress) {
                    setAddress(prev => ({ ...prev, ...order.shippingAddress }));
                }
                if (order.shippingMethod?.id) {
                    setShippingMethod(order.shippingMethod.id);
                }
                if (order.paymentMethod?.id) {
                    setPaymentMethod(order.paymentMethod.id);
                }
                if (order.totals || order.total) {
                    setTotals({
                        subtotal: order.subtotal || order.totals?.subtotal || 0,
                        discount: order.discount || order.totals?.discount || 0,
                        shippingCost: order.shippingCost || order.totals?.shipping || 0,
                        tax: order.gst || order.tax || order.totals?.tax || 0,
                        total: order.total || order.totals?.total || 0
                    });
                    setTotalsCalculated(true);
                }

                setSuccess(`Found pending order #${order.orderNumber}. You can complete payment now.`);
                setPaymentRetry(true);
            }
        } catch (error) {
            debugLog('Checkout', 'Active order check error:', error.message);
        }
    }, [navigate]);

    // 🛒 Merge cart function
    const tryMergeCart = async () => {
        if (hasMergedCart.current) return;
        hasMergedCart.current = true;

        try {
            const guestSessionId = localStorage.getItem('cart_session_id');

            if (guestSessionId && guestSessionId.startsWith("guest_")) {
                debugLog('Checkout', 'Merging guest cart...');
                await CartService.mergeCart(guestSessionId);
                localStorage.removeItem("cart_session_id");
            }

            // Load cart data
            await loadCartData();
            setCartReady(true);

        } catch (error) {
            debugLog('Checkout', 'Cart merge failed:', error.message);
            // Load cart anyway
            await loadCartData();
            setCartReady(true);
        }
    };

    // 🛒 Load cart data
    const loadCartData = async () => {
        try {
            const cartResponse = await CartService.getCart();
            debugLog('Checkout', 'Cart loaded:', cartResponse);

            if (cartResponse?.items) {
                setCart(cartResponse);
                setAppliedOffers(cartResponse.appliedCartOffers || []);

                // Calculate initial totals immediately
                const subtotal = cartResponse.subtotal || cartResponse.items.reduce((sum, item) =>
                    sum + (item.price || 0) * (item.quantity || 1), 0);

                // Set initial totals (will be recalculated later with backend)
                const initialTotals = {
                    subtotal,
                    discount: cartResponse.discountTotal || coupon?.discount || 0,
                    shippingCost: shippingMethod ? (shippingMethod === 'express' ? 99 : 49) : 49,
                    tax: subtotal * 0.18, // 18% GST from your order data
                    total: subtotal + (shippingMethod ? (shippingMethod === 'express' ? 99 : 49) : 49) + (subtotal * 0.18) - (cartResponse.discountTotal || coupon?.discount || 0)
                };

                setTotals(initialTotals);
                debugLog('Checkout', 'Initial totals set:', initialTotals);

                // Return cart for further processing
                return cartResponse;
            }

            return { items: [], subtotal: 0 };
        } catch (error) {
            debugLog('Checkout', 'Load cart error:', error.message);
            setCart({ items: [], subtotal: 0 });
            return { items: [], subtotal: 0 };
        }
    };

    // 📤 Redirect to login
    const redirectToLogin = () => {
        // Save checkout state for recovery
        const checkoutState = {
            address,
            shippingMethod,
            paymentMethod,
            cart: cart?.items || [],
            totals
        };
        sessionStorage.setItem('kromaic_checkout_recovery', JSON.stringify(checkoutState));
        sessionStorage.setItem('kromaic_redirect_after_login', '/checkout');

        navigate('/login', {
            state: {
                from: '/checkout',
                message: 'Please login to complete checkout'
            },
            replace: true
        });
    };

    // 📊 Load checkout data
    const loadCheckoutData = async () => {
        try {
            // Load saved form data
            const savedData = sessionStorage.getItem('kromaic_checkout_form_data');
            if (savedData) {
                setAddress(JSON.parse(savedData));
            }

            // Load user data for auto-fill
            const userData = await loadUserData();
            if (userData) {
                autoFillAddress(userData);
            }

            // Load shipping methods
            await loadShippingMethods();

            // Load payment methods
            await loadPaymentMethods();

            // Calculate totals with backend (after cart is loaded)
            if (cart?.items?.length > 0 && !initialTotalsCalculated.current) {
                debugLog('Checkout', 'Calculating initial totals with backend...');
                await calculateTotals();
                initialTotalsCalculated.current = true;
                setTotalsCalculated(true);
            }

        } catch (error) {
            debugLog('Checkout', 'Load checkout data error:', error.message);
        }
    };

    // 👤 Load user data
    const loadUserData = async () => {
        try {
            const user = AuthService.getStoredUser();
            if (user) {
                // Load addresses after getting user
                await loadUserAddresses();
                return user;
            }

            const authResponse = await AuthService.checkAuth();
            if (authResponse?.user) {
                AuthService.storeUserData(authResponse.user);
                // Load addresses
                await loadUserAddresses();
                return authResponse.user;
            }

            return null;
        } catch (error) {
            debugLog('Checkout', 'Load user error:', error.message);
            return null;
        }
    };

    // 🏠 Load user addresses
    const loadUserAddresses = async () => {
        try {
            setIsLoadingAddresses(true);
            const addresses = await UserService.getAddresses();
            setUserAddresses(addresses);

            // Find default address
            const defaultAddress = addresses.find(addr => addr.isDefault || addr.isPrimary);
            if (defaultAddress) {
                setSelectedAddressId(defaultAddress._id);
                autoFillAddress(defaultAddress);
            }
        } catch (error) {
            debugLog('Checkout', 'Load addresses error:', error.message);
        } finally {
            setIsLoadingAddresses(false);
        }
    };

    // 📝 Auto-fill address from user data or address object
    const autoFillAddress = (addressData) => {
        if (!addressData) return;

        setAddress(prev => {
            const updated = { ...prev };

            if (!updated.name && (addressData.name || addressData.fullName ||
                (addressData.firstName && addressData.lastName))) {
                updated.name = addressData.name || addressData.fullName ||
                    `${addressData.firstName} ${addressData.lastName}`;
            }

            if (!updated.email && addressData.email) {
                updated.email = addressData.email;
            }

            if (!updated.phone && (addressData.phone || addressData.phoneNumber || addressData.mobile)) {
                updated.phone = addressData.phone || addressData.phoneNumber || addressData.mobile;
            }

            if (!updated.address && addressData.address) updated.address = addressData.address;
            if (!updated.city && addressData.city) updated.city = addressData.city;
            if (!updated.state && addressData.state) updated.state = addressData.state;
            if (!updated.zipCode && (addressData.zipCode || addressData.zip || addressData.pincode)) {
                updated.zipCode = addressData.zipCode || addressData.zip || addressData.pincode;
            }
            if (!updated.country && addressData.country) updated.country = addressData.country;

            return updated;
        });
    };

    // 🎯 Handle address selection
    const handleAddressSelect = async (addr) => {
        setSelectedAddressId(addr._id);
        autoFillAddress(addr);

        // If this address is not default, set it as default for checkout
        if (!addr.isDefault && !addr.isPrimary) {
            try {
                await UserService.setDefaultAddress(addr._id);
                // Update local addresses list
                setUserAddresses(prev =>
                    prev.map(address => ({
                        ...address,
                        isDefault: address._id === addr._id
                    }))
                );
            } catch (error) {
                debugLog('Checkout', 'Set default address error:', error.message);
            }
        }
    };

    // 💾 Save new address
    const handleSaveNewAddress = async () => {
        if (!validateAddress()) return;

        setIsSavingAddress(true);
        try {
            const addressData = {
                name: address.name,
                phone: address.phone,
                email: address.email,
                address: address.address,
                city: address.city,
                state: address.state,
                zipCode: address.zipCode,
                country: address.country || 'India',
                isDefault: false
            };

            const savedAddress = await UserService.addAddress(addressData);

            // Add to local list
            setUserAddresses(prev => [...prev, savedAddress]);
            setSelectedAddressId(savedAddress._id);
            setIsAddingNewAddress(false);
            setSaveNewAddress(false); // Reset for next time

            setSuccess('Address saved successfully!');

        } catch (error) {
            setError('Failed to save address: ' + error.message);
        } finally {
            setIsSavingAddress(false);
        }
    };

    // 🚀 Direct Place Order button handler
    const handleDirectPlaceOrder = async () => {
        if (!validateAddress()) return;

        // If user wants to save address and we have a new address
        if (saveNewAddress && !selectedAddressId && address.name) {
            await handleSaveNewAddress();
        }

        // Set default shipping and payment if not selected
        if (!shippingMethod && shippingMethods.length > 0) {
            setShippingMethod(shippingMethods[0].id);
        }

        if (!paymentMethod && paymentMethods.length > 0) {
            setPaymentMethod(paymentMethods[0].id);
        }

        // Recalculate totals
        await calculateTotals();

        // Move to review step
        setCurrentStep(4);
    };

    // 🚚 Load shipping methods
    const loadShippingMethods = async () => {
        try {
            const methods = await CheckoutService.getShippingMethods();
            setShippingMethods(methods);

            if (methods.length > 0 && !shippingMethod) {
                const defaultMethod = methods[0].id;
                setShippingMethod(defaultMethod);
                // Recalculate totals with default shipping
                if (cart?.items?.length > 0) {
                    await calculateTotals();
                }
            }
        } catch (error) {
            debugLog('Checkout', 'Load shipping methods error:', error.message);
            const defaultMethods = [
                { id: 'standard', name: 'Standard Delivery', price: 49, days: '4-7' },
                { id: 'express', name: 'Express Delivery', price: 99, days: '2-3' }
            ];
            setShippingMethods(defaultMethods);
            if (!shippingMethod) {
                const defaultMethod = 'standard';
                setShippingMethod(defaultMethod);
            }
        }
    };

    // 💳 Load payment methods
    const loadPaymentMethods = async () => {
        try {
            const methods = await CheckoutService.getPaymentMethods();
            setPaymentMethods(methods);

            if (methods.length > 0 && !paymentMethod) {
                setPaymentMethod(methods[0].id);
            }
        } catch (error) {
            debugLog('Checkout', 'Load payment methods error:', error.message);
            setPaymentMethods([
                {
                    id: 'razorpay',
                    name: 'Razorpay',
                    description: 'Pay via UPI, Cards, Net Banking',
                    provider: 'razorpay'
                }
            ]);
            setPaymentMethod('razorpay');
        }
    };

    // 🧮 Calculate totals with backend API
    const calculateTotals = async () => {
        if (!cart?.items?.length || !shippingMethod) return;

        try {
            debugLog('Checkout', 'Calculating totals from backend...');

            const payload = {
                items: cart.items.map(item => ({
                    product: item.product?._id || item.productId,
                    variant: item.variant?.id || item.variantId,
                    quantity: item.quantity
                })),
                shippingMethod,
                coupon: coupon?.code
            };

            const result = await CheckoutService.calculateTotals(payload);
            const data = result.data || result;

            debugLog('Checkout', 'Backend totals response:', data);

            // ✅ TRUST BACKEND NUMBERS
            const finalTotals = {
                subtotal: Number(data.subtotal || 0),
                discount: Number(data.discount || 0),
                shippingCost: Number(data.shippingCost || 0),
                tax: Number(data.gst || 0),
                total: Number(data.total || 0)
            };

            setTotals(finalTotals);
            setTotalsCalculated(true);

            debugLog('Checkout', 'Final totals applied:', finalTotals);

        } catch (error) {
            debugLog('Checkout', 'Calculate totals error:', error.message);
            setError('Unable to calculate totals. Please refresh.');
        }
    };

    // 🔄 Auto-calculate totals when shipping method changes
    useEffect(() => {
        if (cartReady && cart?.items?.length > 0 && shippingMethod && dataLoaded) {
            debugLog('Checkout', 'Auto-recalculating totals for shipping change');
            calculateTotals();
        }
    }, [shippingMethod, cartReady, dataLoaded]);

    // 📋 Validation functions - FIXED VERSION
    // Pure validation (NO state updates) - use in render
    const isAddressValid = () => {
        if (!address.name?.trim()) return false;
        if (!address.email?.trim() || !/\S+@\S+\.\S+/.test(address.email)) return false;
        if (!address.phone?.trim() || address.phone.replace(/\D/g, '').length < 10) return false;
        if (!address.address?.trim()) return false;
        if (!address.city?.trim()) return false;
        if (!address.state?.trim()) return false;
        if (!address.zipCode?.trim() || !/^\d{6}$/.test(address.zipCode)) return false;
        return true;
    };

    // State-updating validation (ONLY for submit)
    const validateAddress = () => {
        if (!address.name?.trim()) {
            setError('Please enter your name');
            return false;
        }
        if (!address.email?.trim() || !/\S+@\S+\.\S+/.test(address.email)) {
            setError('Please enter a valid email');
            return false;
        }
        if (!address.phone?.trim() || address.phone.replace(/\D/g, '').length < 10) {
            setError('Please enter a valid 10-digit phone number');
            return false;
        }
        if (!address.address?.trim()) {
            setError('Please enter your address');
            return false;
        }
        if (!address.city?.trim()) {
            setError('Please enter your city');
            return false;
        }
        if (!address.state?.trim()) {
            setError('Please enter your state');
            return false;
        }
        if (!address.zipCode?.trim() || !/^\d{6}$/.test(address.zipCode)) {
            setError('Please enter a valid 6-digit PIN code');
            return false;
        }

        return true;
    };

    // 🚀 Handle next step - UPDATED for address saving
    const handleNextStep = () => {
        setError('');
        setSuccess('');

        // Check if cart is empty (unless we have active order)
        if (!activeOrder && (!cart?.items || cart.items.length === 0)) {
            setError('Your cart is empty. Please add items before checkout.');
            setTimeout(() => navigate('/shop'), 1500);
            return;
        }

        if (currentStep < 4) {
            let isValid = true;

            switch (currentStep) {
                case 1:
                    // Validate address using state-updating validation
                    isValid = validateAddress();

                    if (isValid && saveNewAddress && !selectedAddressId && !isAddingNewAddress) {
                        // Save address if checkbox is checked and it's a new address
                        handleSaveNewAddress().then(() => {
                            // After saving, move to next step
                            setCurrentStep(2);
                        }).catch(() => {
                            // Error will be shown by handleSaveNewAddress
                        });
                        return;
                    }
                    break;
                case 2:
                    if (!shippingMethod) {
                        setError('Please select a shipping method');
                        isValid = false;
                    }
                    break;
                case 3:
                    if (!paymentMethod) {
                        setError('Please select a payment method');
                        isValid = false;
                    }
                    break;
            }

            if (isValid && (currentStep !== 1 || (currentStep === 1 && (selectedAddressId || isAddingNewAddress)))) {
                setCurrentStep(prev => prev + 1);
            }
        } else {
            handlePlaceOrder();
        }
    };

    // ↩️ Handle previous step
    const handlePrevStep = () => {
        if (currentStep > 1) {
            if (currentStep === 1 && isAddingNewAddress && userAddresses.length > 0) {
                setIsAddingNewAddress(false);
            } else {
                setCurrentStep(prev => prev - 1);
            }
        }
    };

    // Add cancel payment handler
    const handleCancelPayment = async () => {
        if (!activeOrder) return;

        if (!window.confirm('Are you sure you want to cancel this payment? You can retry payment later from your orders page.')) {
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const result = await PaymentService.cancelPayment({
                orderId: activeOrder._id
            });

            if (result.success) {
                // Clear active order
                setActiveOrder(null);
                setPaymentRetry(false);
                setRecoveringPayment(false);
                setCanCancelPayment(false);

                // Remove from session storage
                sessionStorage.removeItem('kromaic_active_order');
                sessionStorage.removeItem('kromaic_payment_failure');

                setSuccess('Payment cancelled. You can retry anytime from your orders page.');

                // Optional: navigate back to cart or stay
                setTimeout(() => {
                    navigate('/orders');
                }, 3000);
            } else {
                throw new Error(result.message || 'Failed to cancel payment');
            }
        } catch (error) {
            console.error('Cancel payment error:', error);
            setError(error.message || 'Failed to cancel payment');
        } finally {
            setIsLoading(false);
        }
    };

    // 🎟️ Apply coupon
    const handleApplyCoupon = async (code) => {
        if (!code.trim()) {
            setError('Please enter coupon code');
            return;
        }

        try {
            const result = await CheckoutService.applyOffer(code);
            if (result.success) {
                setCoupon({ code, discount: result.discount || 0 });
                setAppliedOffers(result.appliedOffers || []);
                setSuccess('Coupon applied successfully!');
                await calculateTotals();
            } else {
                setError(result.message || 'Invalid coupon');
            }
        } catch (error) {
            setError('Failed to apply coupon');
        }
    };

    // 🗑️ Remove coupon
    const handleRemoveCoupon = async () => {
        if (!coupon?.code) return;

        try {
            const result = await CheckoutService.removeOffer(coupon.code);
            if (result.success) {
                setCoupon(null);
                setAppliedOffers(result.appliedOffers || []);
                setSuccess('Coupon removed');
                await calculateTotals();
            }
        } catch (error) {
            setError('Failed to remove coupon');
        }
    };

    // 📦 Main order placement
    const handlePlaceOrder = async () => {
        debugLog('Checkout', 'handlePlaceOrder started');

        // If we have active order, continue payment
        if (activeOrder) {
            await continuePayment();
            return;
        }

        // Validate cart
        if (!cart?.items || cart.items.length === 0) {
            setError('Your cart is empty');
            return;
        }

        // Validate form
        if (!validateAddress()) return;
        if (!shippingMethod) {
            setError('Please select shipping method');
            return;
        }
        if (!paymentMethod) {
            setError('Please select payment method');
            return;
        }

        // Ensure totals are calculated
        if (!totalsCalculated) {
            setError('Please wait while we calculate your order totals...');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Prepare order data
            const orderData = {
                items: cart.items.map(item => ({
                    product: item.product?._id || item.productId,
                    variant: item.variant?.id || item.variantId,
                    quantity: item.quantity,
                    price: item.price,
                    name: item.product?.name || item.name
                })),
                shippingAddress: {
                    ...address,
                    addressId: selectedAddressId // Include address ID for reference
                },
                shippingMethod: {
                    id: shippingMethod,
                    name: shippingMethods.find(m => m.id === shippingMethod)?.name || shippingMethod
                },
                paymentMethod: {
                    id: paymentMethod,
                    name: paymentMethods.find(m => m.id === paymentMethod)?.name || paymentMethod,
                    provider: 'razorpay'
                },
                totals: {
                    subtotal: totals.subtotal,
                    discount: totals.discount,
                    shipping: totals.shippingCost,
                    tax: totals.tax,
                    total: totals.total
                },
                appliedOffers: appliedOffers,
                coupon: coupon?.code
            };

            debugLog('Checkout', 'Creating order:', orderData);

            // Step 1: Create order
            const orderResult = await CheckoutService.confirmCheckout(orderData);

            if (!orderResult.success) {
                throw new Error(orderResult.message || 'Failed to create order');
            }

            const order = orderResult.data;
            setCreatedOrder(order);
            setActiveOrder(order);

            // Save order ID in session storage for recovery
            sessionStorage.setItem('kromaic_active_order', JSON.stringify({
                id: order._id,
                orderNumber: order.orderNumber,
                total: order.total,
                paymentStatus: order.paymentStatus
            }));

            debugLog('Checkout', 'Order created:', order.orderNumber);

            // Step 2: Create payment intent
            setPaymentProcessing(true);
            await createPaymentIntent(order);

        } catch (error) {
            debugLog('Checkout', 'Order creation error:', error.message);
            setError(error.message || 'Failed to place order');
            setIsLoading(false);
        }
    };

    // 💳 Create payment intent
    const createPaymentIntent = async (order) => {
        try {
            debugLog('Checkout', 'Creating payment intent for order:', order._id);

            const paymentData = await CheckoutService.createPaymentIntent(
                order._id,
                'razorpay',
                paymentMethod
            );

            if (!paymentData.success) {
                throw new Error(paymentData.message || 'Failed to create payment');
            }

            setPaymentIntent(paymentData.data || paymentData);

            // Step 3: Open Razorpay
            await openRazorpay(paymentData.data || paymentData, order);

        } catch (error) {
            debugLog('Checkout', 'Payment intent error:', error.message);
            setError(error.message || 'Payment setup failed');
            setPaymentProcessing(false);
            setIsLoading(false);
        }
    };

    // 🔄 Continue payment for existing order
    const continuePayment = async () => {
        if (!activeOrder) return;

        setPaymentProcessing(true);
        setError('');

        try {
            debugLog('Checkout', 'Continuing payment for order:', activeOrder._id);

            // Create new payment intent
            const paymentData = await CheckoutService.createPaymentIntent(
                activeOrder._id,
                'razorpay',
                paymentMethod || activeOrder.paymentMethod?.id || 'razorpay'
            );

            if (!paymentData.success) {
                throw new Error(paymentData.message || 'Failed to create payment');
            }

            await openRazorpay(paymentData.data || paymentData, activeOrder);

        } catch (error) {
            debugLog('Checkout', 'Continue payment error:', error.message);
            setError(error.message || 'Failed to continue payment');
            setPaymentProcessing(false);
        }
    };

    const openRazorpay = async (paymentData, order) => {
        try {
            // Load Razorpay script
            if (!razorpayLoaded.current) {
                await loadRazorpayScript();
                razorpayLoaded.current = true;
            }

            if (!window.Razorpay) {
                throw new Error('Razorpay SDK failed to load');
            }

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: paymentData.amount || Math.round(order.total * 100),
                currency: paymentData.currency || 'INR',
                name: 'Your Store',
                description: `Order #${order.orderNumber}`,
                order_id: paymentData.razorpayOrderId,
                handler: async (response) => {
                    await verifyPayment(response, order);
                },
                prefill: {
                    name: address.name,
                    email: address.email,
                    contact: address.phone
                },
                theme: { color: '#C19A6B' },
                modal: {
                    ondismiss: async () => {
                        console.log('🔴 Modal dismissed by user');
                        setPaymentProcessing(false);
                        setIsLoading(false);

                        // Call cleanup API to reset payment state
                        try {
                            await PaymentService.markPaymentFailed(
                                order._id,
                                'Payment cancelled by user'
                            );
                            setSuccess('Payment cancelled. You can retry anytime.');
                        } catch (error) {
                            console.log('Cleanup error:', error);
                            setSuccess('Payment cancelled. You can retry anytime.');
                        }
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();

        } catch (error) {
            console.error('Razorpay error:', error);
            setError('Failed to open payment gateway');
            setPaymentProcessing(false);
            setIsLoading(false);
        }
    };

    // 📝 Load Razorpay script
    const loadRazorpayScript = () => {
        return new Promise((resolve, reject) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => reject(new Error('Failed to load Razorpay'));
            document.body.appendChild(script);
        });
    };

    // ✅ Verify payment
    const verifyPayment = async (razorpayResponse, order) => {
        try {
            debugLog('Checkout', 'Verifying payment...', razorpayResponse);

            // Add a small delay to ensure Razorpay modal is closed
            await new Promise(resolve => setTimeout(resolve, 1000));

            debugLog('Checkout', 'Calling PaymentService.verifyRazorpayPayment...');
            const verification = await PaymentService.verifyRazorpayPayment(
                razorpayResponse.razorpay_payment_id,
                razorpayResponse.razorpay_order_id,
                razorpayResponse.razorpay_signature
            );

            debugLog('Checkout', 'Verification response:', verification);

            if (!verification?.success) {
                throw new Error(
                    verification?.message ||
                    verification?.error ||
                    verification?.data?.message ||
                    'Payment verification failed'
                );
            }

            debugLog('Checkout', '✅ Payment verified successfully');

            // Wait a bit more to ensure backend updates are complete
            await new Promise(resolve => setTimeout(resolve, 500));

            // ✅ Complete order only after verification
            await completeOrder(order);

        } catch (error) {
            debugLog('Checkout', '❌ Payment verification error', error);

            // Enhanced error logging
            console.error('🔥 Payment verification details:', {
                errorName: error.name,
                errorMessage: error.message,
                errorStack: error.stack,
                razorpayResponse,
                order: {
                    id: order._id,
                    orderNumber: order.orderNumber
                }
            });

            // More specific error messages
            let errorMessage = 'Payment verification failed';
            if (error.message.includes('400')) {
                errorMessage = 'Payment validation failed. Please contact support.';
            } else if (error.message.includes('404')) {
                errorMessage = 'Payment record not found. Please refresh and try again.';
            } else if (error.message.includes('500')) {
                errorMessage = 'Server error during payment verification. Please try again.';
            } else if (error.message.includes('network')) {
                errorMessage = 'Network error. Please check your connection and try again.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
            setPaymentProcessing(false);
            setIsLoading(false);

            // Save payment failure for retry
            if (order?._id) {
                sessionStorage.setItem('kromaic_payment_failure', JSON.stringify({
                    orderId: order._id,
                    orderNumber: order.orderNumber,
                    error: errorMessage,
                    timestamp: new Date().toISOString()
                }));
            }
        }
    };

    // 🎊 Complete order
    const completeOrder = async (order) => {
        try {
            debugLog('Checkout', '🎊 Starting order completion...', {
                orderId: order._id,
                orderNumber: order.orderNumber
            });

            // Step 1: Get fresh order data from backend with verification
            debugLog('Checkout', '🔄 Fetching updated order data...');

            let freshOrder = null;
            let attempts = 0;
            const maxAttempts = 5;
            const delayBetweenAttempts = 1000; // 1 second

            // Keep trying until we get updated payment status or max attempts reached
            while (attempts < maxAttempts) {
                attempts++;
                debugLog('Checkout', `📡 Fetch attempt ${attempts}/${maxAttempts} for order:`, order._id);

                try {
                    const orderResponse = await OrderService.getOrderById(order._id);

                    if (orderResponse?.success && orderResponse?.data) {
                        freshOrder = orderResponse.data;

                        debugLog('Checkout', '📊 Fresh order data fetched:', {
                            orderNumber: freshOrder.orderNumber,
                            paymentStatus: freshOrder.paymentStatus,
                            orderStatus: freshOrder.orderStatus,
                            total: freshOrder.total
                        });

                        // Check if payment status is updated
                        if (freshOrder.paymentStatus === 'completed' || freshOrder.paymentStatus === 'paid') {
                            debugLog('Checkout', '✅ Payment status updated successfully!');
                            break;
                        } else {
                            debugLog('Checkout', '⏳ Payment status still pending:', freshOrder.paymentStatus);
                            // Wait before next attempt
                            await new Promise(resolve => setTimeout(resolve, delayBetweenAttempts));
                        }
                    }
                } catch (fetchError) {
                    debugLog('Checkout', `⚠️ Fetch attempt ${attempts} failed:`, fetchError.message);
                    if (attempts < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, delayBetweenAttempts));
                    }
                }
            }

            // If we still don't have updated data, use the original with manual override
            if (!freshOrder || (freshOrder.paymentStatus !== 'completed' && freshOrder.paymentStatus !== 'paid')) {
                debugLog('Checkout', '⚠️ Using original order with payment override');
                freshOrder = {
                    ...order,
                    paymentStatus: 'completed',
                    orderStatus: 'confirmed',
                    updatedAt: new Date().toISOString()
                };

                // Make one final attempt to sync with backend
                try {
                    const finalCheck = await OrderService.getOrderById(order._id);
                    if (finalCheck?.success && finalCheck?.data?.paymentStatus === 'completed') {
                        freshOrder = finalCheck.data;
                    }
                } catch (finalError) {
                    debugLog('Checkout', 'Final sync check failed:', finalError.message);
                }
            }

            // Step 2: Clear cart and storage
            debugLog('Checkout', '🗑️ Clearing cart and storage...');
            try {
                // Clear cart in background
                CartService.clearCart().catch(err =>
                    debugLog('Checkout', '⚠️ Cart clear error (non-critical):', err.message)
                );

                // Clear all checkout-related storage
                const storageKeys = [
                    'kromaic_checkout_form_data',
                    'kromaic_active_order',
                    'kromaic_payment_failure',
                    'kromaic_payment_error',
                    'kromaic_checkout_recovery'
                ];

                storageKeys.forEach(key => sessionStorage.removeItem(key));
                localStorage.removeItem('cart_session_id');

                debugLog('Checkout', '✅ Storage cleared');

            } catch (storageError) {
                debugLog('Checkout', '⚠️ Storage clear error:', storageError.message);
            }

            // Step 3: Prepare success data
            const successData = {
                orderId: freshOrder._id || order._id,
                orderNumber: freshOrder.orderNumber || order.orderNumber,
                items: freshOrder.orderItems || order.orderItems || [],
                address: freshOrder.shippingAddress || order.shippingAddress || {},
                paymentMethod: freshOrder.paymentMethod?.id ||
                    order.paymentMethod?.id ||
                    'razorpay',
                total: freshOrder.total || order.total || 0,
                estimatedDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                orderStatus: freshOrder.orderStatus || 'confirmed',
                paymentStatus: freshOrder.paymentStatus || 'completed',
                fromRecovery: !!activeOrder,
                timestamp: new Date().toISOString(),
                // Add metadata for debugging
                _debug: {
                    backendPaymentStatus: freshOrder.paymentStatus,
                    backendOrderStatus: freshOrder.orderStatus,
                    attemptsMade: attempts,
                    source: freshOrder === order ? 'original' : 'backend'
                }
            };

            debugLog('Checkout', '📦 Success data prepared:', {
                orderNumber: successData.orderNumber,
                paymentStatus: successData.paymentStatus,
                orderStatus: successData.orderStatus,
                items: successData.items.length
            });

            // Step 4: Final delay for smoother transition
            debugLog('Checkout', '⏳ Adding final delay...');
            await new Promise(resolve => setTimeout(resolve, 800));

            // Step 5: Navigate to success page
            debugLog('Checkout', '✅ NAVIGATING TO SUCCESS PAGE');

            navigate('/order-success', {
                state: successData,
                replace: true
            });

        } catch (error) {
            debugLog('Checkout', '❌ Error in completeOrder:', error);

            // Emergency fallback - navigate with minimal data
            console.error('🔥 Complete order error:', error);

            const fallbackData = {
                orderId: order._id,
                orderNumber: order.orderNumber,
                total: order.total || 0,
                paymentMethod: 'razorpay',
                orderStatus: 'confirmed',
                paymentStatus: 'completed',
                errorOccurred: true,
                errorMessage: 'Order completed successfully but some details may be delayed'
            };

            // Clear storage
            try {
                sessionStorage.removeItem('kromaic_checkout_form_data');
                sessionStorage.removeItem('kromaic_active_order');
                localStorage.removeItem('cart_session_id');
            } catch (e) {}

            navigate('/order-success', {
                state: fallbackData,
                replace: true
            });
        }
    };

    // 🎨 Render step content - UPDATED for step 1 with address management
    const renderStepContent = () => {
        if (!dataLoaded || (!activeOrder && !totalsCalculated)) {
            return (
                <div className="py-12">
                    <div className="flex justify-center">
                        <LuxuryLoading size="lg" message="Loading checkout details..." />
                    </div>
                </div>
            );
        }

        switch(currentStep) {
            case 1:
                return (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-heading text-2xl font-medium text-charcoal">
                                Shipping Address
                            </h2>
                            {userAddresses.length > 0 && !isAddingNewAddress && (
                                <button
                                    onClick={() => setIsAddingNewAddress(true)}
                                    className="text-champagne hover:text-champagne/80 transition-colors text-sm font-medium flex items-center gap-2"
                                >
                                    <FaMapMarkerAlt className="w-4 h-4" />
                                    Add New Address
                                </button>
                            )}
                        </div>

                        {/* Loading state for addresses */}
                        {isLoadingAddresses ? (
                            <div className="py-8 text-center">
                                <div className="w-12 h-12 border-4 border-champagne/30 border-t-champagne rounded-full animate-spin mx-auto mb-4"></div>
                                <p className="text-charcoal/60">Loading your addresses...</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Saved Addresses List */}
                                {userAddresses.length > 0 && !isAddingNewAddress ? (
                                    <div className="space-y-4">
                                        <div className="grid gap-4">
                                            {userAddresses.map((addr) => (
                                                <div
                                                    key={addr._id}
                                                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                                                        selectedAddressId === addr._id
                                                            ? 'border-champagne bg-champagne/5 ring-1 ring-champagne/30'
                                                            : 'border-nude bg-white hover:border-champagne'
                                                    }`}
                                                    onClick={() => handleAddressSelect(addr)}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-start gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-nude/30 flex items-center justify-center flex-shrink-0">
                                                                <FaMapMarkerAlt className="w-4 h-4 text-charcoal/50" />
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="font-medium text-charcoal">
                                                                        {addr.name}
                                                                    </span>
                                                                    {addr.isDefault && (
                                                                        <span className="text-xs px-2 py-1 bg-sage/20 text-sage rounded-full">
                                                                            Default
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p className="text-sm text-charcoal/60">
                                                                    {addr.address}, {addr.city}, {addr.state} {addr.zipCode}
                                                                </p>
                                                                <p className="text-sm text-charcoal/50 mt-1">
                                                                    📱 {addr.phone}
                                                                    {addr.email && ` • ✉️ ${addr.email}`}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {selectedAddressId === addr._id && (
                                                            <FaCheckCircle className="w-5 h-5 text-champagne" />
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="flex items-center justify-between pt-4 border-t border-nude/30">
                                            <button
                                                onClick={() => setIsAddingNewAddress(true)}
                                                className="text-champagne hover:text-champagne/80 transition-colors text-sm font-medium flex items-center gap-2"
                                            >
                                                <FaMapMarkerAlt className="w-4 h-4" />
                                                Add New Address
                                            </button>

                                            {/* Direct Place Order Button - FIXED: use pure validation */}
                                            {selectedAddressId && (
                                                <button
                                                    onClick={handleDirectPlaceOrder}
                                                    disabled={!isAddressValid()}
                                                    className="px-4 py-2 bg-gradient-to-r from-champagne to-sage text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                >
                                                    <FaArrowRight className="w-4 h-4" />
                                                    Direct Place Order
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    /* Address Form */
                                    <div>
                                        <AddressForm
                                            address={address}
                                            onChange={setAddress}
                                            errors={{}}
                                            savedAddresses={userAddresses}
                                            onSelectSavedAddress={(selectedAddr) => {
                                                handleAddressSelect(selectedAddr);
                                                setIsAddingNewAddress(false);
                                            }}
                                        />

                                        <div className="mt-6 p-4 bg-gradient-to-r from-pearl to-nude/30 rounded-xl">
                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={saveNewAddress}
                                                    onChange={(e) => setSaveNewAddress(e.target.checked)}
                                                    className="mt-1 w-4 h-4 rounded border-nude text-champagne focus:ring-champagne/30"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <FaSave className="w-4 h-4 text-champagne" />
                                                        <span className="font-medium text-charcoal">
                                                            Save this address for future orders
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-charcoal/50">
                                                        Your address will be securely stored for faster checkout
                                                    </p>
                                                </div>
                                            </label>
                                        </div>

                                        <div className="flex justify-between items-center mt-8 pt-6 border-t border-nude/30">
                                            {userAddresses.length > 0 && (
                                                <button
                                                    onClick={() => setIsAddingNewAddress(false)}
                                                    className="text-charcoal/60 hover:text-charcoal transition-colors text-sm font-medium flex items-center gap-2"
                                                >
                                                    <FaArrowLeft className="w-4 h-4" />
                                                    Back to Saved Addresses
                                                </button>
                                            )}

                                            {/* Direct Place Order Button for new address - FIXED: use pure validation */}
                                            <div className="flex items-center gap-4">
                                                <button
                                                    onClick={() => {
                                                        if (validateAddress()) {
                                                            if (saveNewAddress && !selectedAddressId) {
                                                                handleSaveNewAddress().then(() => {
                                                                    handleNextStep();
                                                                });
                                                                return;
                                                            }
                                                            handleNextStep();
                                                        }
                                                    }}
                                                    className="px-4 py-2 bg-gradient-to-r from-champagne to-sage text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
                                                    disabled={isSavingAddress}
                                                >
                                                    {isSavingAddress ? (
                                                        <>
                                                            <FaSpinner className="w-4 h-4 animate-spin" />
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaArrowRight className="w-4 h-4" />
                                                            Continue to Shipping
                                                        </>
                                                    )}
                                                </button>

                                                <button
                                                    onClick={handleDirectPlaceOrder}
                                                    disabled={!isAddressValid() || isSavingAddress}
                                                    className="px-4 py-2 bg-white border-2 border-champagne text-champagne rounded-full text-sm font-medium hover:bg-champagne/5 transition-colors flex items-center gap-2"
                                                >
                                                    <FaCheckCircle className="w-4 h-4" />
                                                    Direct Place Order
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );

            case 2:
                return (
                    <div>
                        <h2 className="font-heading text-2xl font-medium text-charcoal mb-6">
                            Shipping Method
                        </h2>
                        <ShippingOptions
                            selected={shippingMethod}
                            onSelect={async (method) => {
                                setShippingMethod(method);
                                await calculateTotals();
                            }}
                            methods={shippingMethods}
                        />
                    </div>
                );

            case 3:
                return (
                    <div>
                        <h2 className="font-heading text-2xl font-medium text-charcoal mb-6">
                            Payment Method
                        </h2>
                        <PaymentMethod
                            method={paymentMethod}
                            onSelect={setPaymentMethod}
                            methods={paymentMethods}
                        />
                    </div>
                );

            case 4:
                return (
                    <div>
                        <h2 className="font-heading text-2xl font-medium text-charcoal mb-6">
                            Review Your Order
                        </h2>

                        {/* Order status indicator - INTEGRATED CODE */}
                        {activeOrder && (
                            <div className="mb-6">
                                <div className={`p-4 rounded-xl border ${
                                    activeOrder.paymentStatus === 'failed'
                                        ? 'border-red-300 bg-red-50'
                                        : 'border-sage/30 bg-sage/10'
                                }`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {activeOrder.paymentStatus === 'failed' ? (
                                                <FaExclamationCircle className="w-5 h-5 text-red-500" />
                                            ) : (
                                                <FaHistory className="w-5 h-5 text-sage" />
                                            )}
                                            <div>
                                                <p className={`font-medium ${
                                                    activeOrder.paymentStatus === 'failed'
                                                        ? 'text-red-600'
                                                        : 'text-sage'
                                                }`}>
                                                    {activeOrder.paymentStatus === 'failed'
                                                        ? 'Payment Failed'
                                                        : 'Continue Payment'}
                                                </p>
                                                <p className="text-sm text-charcoal/60">
                                                    Order #{activeOrder.orderNumber} • ₹{activeOrder.total}
                                                </p>
                                            </div>
                                        </div>

                                        {activeOrder.paymentStatus === 'failed' && (
                                            <button
                                                onClick={() => {
                                                    // Force cleanup and retry
                                                    handleDirectPlaceOrder();
                                                }}
                                                className="px-4 py-2 bg-gradient-to-r from-champagne to-sage text-white rounded-full text-sm font-medium hover:opacity-90"
                                            >
                                                Retry Payment
                                            </button>
                                        )}
                                    </div>

                                    {activeOrder.paymentStatus === 'failed' && (
                                        <p className="text-sm text-red-500 mt-2">
                                            Last payment attempt failed. Click "Retry Payment" to try again.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Shipping Info */}
                        <div className="p-6 rounded-2xl border border-nude/50 bg-gradient-to-br from-pearl to-transparent mb-6">
                            <h3 className="font-medium text-charcoal mb-4">Shipping Information</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-charcoal/50 mb-1">Name</p>
                                    <p className="font-medium text-charcoal">{address.name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-charcoal/50 mb-1">Email</p>
                                    <p className="font-medium text-charcoal">{address.email}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-charcoal/50 mb-1">Phone</p>
                                    <p className="font-medium text-charcoal">{address.phone}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-charcoal/50 mb-1">Shipping Method</p>
                                    <p className="font-medium text-charcoal">
                                        {shippingMethods.find(m => m.id === shippingMethod)?.name || shippingMethod}
                                    </p>
                                </div>
                                <div className="md:col-span-2">
                                    <p className="text-sm text-charcoal/50 mb-1">Address</p>
                                    <p className="font-medium text-charcoal">
                                        {address.address}, {address.city}, {address.state} {address.zipCode}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Payment Info */}
                        <div className="p-6 rounded-2xl border border-nude/50 bg-gradient-to-br from-pearl to-transparent mb-6">
                            <h3 className="font-medium text-charcoal mb-4">Payment Method</h3>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-nude/30 flex items-center justify-center">
                                    {paymentMethod === 'razorpay' ? (
                                        <SiRazorpay className="w-5 h-5 text-champagne" />
                                    ) : (
                                        <FaCreditCard className="w-5 h-5 text-champagne" />
                                    )}
                                </div>
                                <div>
                                    <p className="font-medium text-charcoal">
                                        {paymentMethods.find(m => m.id === paymentMethod)?.name || paymentMethod}
                                    </p>
                                    <p className="text-sm text-charcoal/60">
                                        Pay via UPI, Cards, Net Banking
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Trust Assurance */}
                        <div className="p-6 rounded-2xl bg-gradient-to-r from-sage/5 to-champagne/5">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                                    <FaLock className="w-5 h-5 text-sage" />
                                </div>
                                <div>
                                    <h4 className="font-medium text-charcoal mb-2">Secure Payment</h4>
                                    <p className="text-sm text-charcoal/60">
                                        Your payment is secured with bank-level encryption.
                                        All transactions are PCI-DSS compliant.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // 🏷️ Render loading states
    if (isAuthenticating || !dataLoaded) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/10 flex items-center justify-center">
                <LuxuryLoading
                    size="xl"
                    message="Preparing your checkout experience..."
                    showSpinner={true}
                    variant="elegant"
                />
            </div>
        );
    }

    // 🛒 Empty cart state
    if (!activeOrder && (!cart?.items || cart.items.length === 0)) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/10 flex items-center justify-center">
                <div className="text-center max-w-md px-4">
                    <div className="w-20 h-20 rounded-full bg-champagne/10 flex items-center justify-center mx-auto mb-6">
                        <FaShoppingBag className="w-10 h-10 text-champagne" />
                    </div>
                    <h2 className="text-2xl font-medium text-charcoal mb-3">Your Cart is Empty</h2>
                    <p className="text-charcoal/60 mb-6">
                        Add products to your cart before checking out
                    </p>
                    <LuxuryButton
                        variant="primary"
                        onClick={() => navigate('/shop')}
                        icon={FaShoppingBag}
                        iconPosition="left"
                    >
                        Shop Now
                    </LuxuryButton>
                </div>
            </div>
        );
    }

    // Show loading while calculating totals
    if (!activeOrder && (!totalsCalculated || !cartReady)) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/10">
                <div className="fixed top-0 left-0 right-0 z-40 bg-white/100 backdrop-blur-sm shadow-soft">
                    <Navbar />
                </div>
                <main className="container-luxury max-w-7xl mx-auto px-4 py-8 md:py-12 pt-20">
                    <div className="flex justify-center items-center min-h-[60vh]">
                        <LuxuryLoading
                            size="lg"
                            message="Calculating your order totals..."
                            showSpinner={true}
                        />
                    </div>
                </main>
            </div>
        );
    }

    return (
        <motion.div
            variants={checkoutPageTransition}
            initial="initial"
            animate="animate"
            exit="exit"
            className="min-h-screen bg-gradient-to-b from-pearl to-nude/10"
        >
            {/* Messages */}
            <AnimatePresence>
                {(error || success) && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full px-4"
                    >
                        <div className={`rounded-xl p-4 shadow-lg ${
                            error ? 'bg-white border border-error/30' : 'bg-sage/10 border border-sage/30'
                        }`}>
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    {error ? (
                                        <FaExclamationCircle className="w-5 h-5 text-error flex-shrink-0" />
                                    ) : (
                                        <FaCheckCircle className="w-5 h-5 text-sage flex-shrink-0" />
                                    )}
                                    <p className={`text-sm font-medium ${
                                        error ? 'text-charcoal' : 'text-sage'
                                    }`}>
                                        {error || success}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setError('');
                                        setSuccess('');
                                    }}
                                    className="text-charcoal/40 hover:text-charcoal/60 transition-colors flex-shrink-0"
                                >
                                    <FaTimes className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Navbar */}
            <div className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
                isScrolled ? 'bg-white/100 backdrop-blur-sm shadow-soft' : 'bg-transparent'
            }`}>
                <Navbar />
            </div>

            {/* Payment Processing Overlay */}
            <AnimatePresence>
                {paymentProcessing && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center"
                    >
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="bg-white rounded-3xl p-8 max-w-md text-center"
                        >
                            <div className="w-16 h-16 border-4 border-champagne/30 border-t-champagne rounded-full animate-spin mx-auto mb-4"></div>
                            <h3 className="text-xl font-medium text-charcoal mb-2">
                                Opening Payment Gateway...
                            </h3>
                            <p className="text-charcoal/60">
                                Please wait while we open the secure payment window
                            </p>
                            <p className="text-sm text-charcoal/40 mt-2">
                                Do not refresh or close this page
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <main className="container-luxury max-w-7xl mx-auto px-4 py-8 md:py-12 pt-20">
                <CheckoutHeroBit />

                <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
                    {/* Left: Checkout Form */}
                    <div className="lg:col-span-2">
                        <StepIndicator
                            currentStep={currentStep}
                            onStepClick={(step) => {
                                if (step < currentStep) {
                                    setCurrentStep(step);
                                }
                            }}
                        />

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentStep}
                                variants={stepTransition}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                className="mb-8"
                            >
                                {renderStepContent()}
                            </motion.div>
                        </AnimatePresence>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between pt-8 border-t border-nude/30">
                            {currentStep > 1 ? (
                                <LuxuryButton
                                    variant="outline"
                                    onClick={handlePrevStep}
                                    icon={FaArrowLeft}
                                    iconPosition="left"
                                    disabled={isLoading || paymentProcessing}
                                >
                                    Back
                                </LuxuryButton>
                            ) : (
                                <div />
                            )}

                            <LuxuryButton
                                variant="primary"
                                onClick={handleNextStep}
                                icon={currentStep === 4 ? FaCheckCircle : FaArrowRight}
                                iconPosition="right"
                                isLoading={isLoading || paymentProcessing || isSavingAddress}
                                disabled={isLoading || paymentProcessing || isSavingAddress || (!totalsCalculated && currentStep === 4)}
                            >
                                {activeOrder ? 'Continue Payment' :
                                    paymentRetry ? 'Retry Payment' :
                                        currentStep === 4 ? 'Place Order & Pay' : 'Continue'}
                            </LuxuryButton>
                        </div>
                    </div>

                    {/* Right: Order Summary */}
                    <div className="lg:col-span-1">
                        <OrderSummary
                            items={cart?.items || []}
                            subtotal={totals.subtotal}
                            shipping={totals.shippingCost}
                            tax={totals.tax}
                            total={totals.total}
                            discount={totals.discount}
                            coupon={coupon}
                            appliedOffers={appliedOffers}
                            onApplyCoupon={handleApplyCoupon}
                            onRemoveCoupon={handleRemoveCoupon}
                            isLoading={isLoading || paymentProcessing || !totalsCalculated || isSavingAddress}
                            freeShippingEligible={totals.subtotal >= 2000}
                            isCalculating={!totalsCalculated}
                        />
                    </div>
                </div>

                {/* Trust Bar */}
                <div className="mt-12 pt-12 border-t border-nude/30">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { icon: FaShieldAlt, title: "Secure Payment", desc: "Bank-level encryption" },
                            { icon: FaCheckCircle, title: "30-Day Returns", desc: "Easy returns policy" },
                            { icon: FaLock, title: "PCI DSS Compliant", desc: "Secure processing" },
                            { icon: FaShieldAlt, title: "GST Invoice", desc: "GST invoice on all orders" }
                        ].map((item, index) => (
                            <div key={index} className="text-center">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-champagne/10 to-sage/10 flex items-center justify-center mx-auto mb-3">
                                    <item.icon className="w-5 h-5 text-champagne" />
                                </div>
                                <h4 className="text-sm font-medium text-charcoal mb-1">{item.title}</h4>
                                <p className="text-xs text-charcoal/40">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            <Footer />
        </motion.div>
    );
};

export default Checkout;